import { createContext, useContext, useState, useMemo, useEffect } from 'react';
import {
  signInWithPopup,
  getRedirectResult,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import type { AdminUser } from '../types';
import { createGoogleAuthProvider, getFirebaseAuth, isFirebaseConfigured } from '../lib/firebase';
import { getAdminPhone } from '../lib/adminProfileApi';
import { isFirebasePopupClosedByUser } from '../../../shared/lib/isFirebasePopupClosedByUser';
import { getFirebaseAuthErrorCode, getFirebaseAuthErrorMessage } from '../../../shared/lib/firebaseAuthMessages';

const AUTH_STORAGE_KEY = 'authUserAdmin';

/** Única conta autorizada no painel admin (não configurável por .env). */
const ADMIN_EMAIL_ALLOWED = 'diegonavi82@gmail.com';

function loadStoredUser(): AdminUser | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw) as AdminUser;
    if (u?.userId && u?.email && u?.name) return u;
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }
  return null;
}

function getProviderFromFirebase(fb: FirebaseUser): AdminUser['provider'] {
  const first = fb.providerData[0];
  if (first?.providerId === 'google.com') return 'google';
  if (first?.providerId === 'password') return 'email';
  return 'google'; // fallback
}

function firebaseUserToAdminUser(fb: FirebaseUser, phone?: string): AdminUser {
  return {
    userId: fb.uid,
    email: fb.email ?? '',
    name: fb.displayName ?? fb.email ?? 'Admin',
    phone: phone ?? undefined,
    imageUrl: fb.photoURL ?? undefined,
    provider: getProviderFromFirebase(fb),
  };
}

function isAdminEmail(email: string): boolean {
  return email.trim().toLowerCase() === ADMIN_EMAIL_ALLOWED;
}

interface AuthContextType {
  user: AdminUser | null;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  loginWithGmail: () => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => void;
  isFirebaseReady: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(loadStoredUser);
  const isFirebaseReady = isFirebaseConfigured();

  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth) return;

    // Processa retorno do redirect do Google (evita popup bloqueado)
    void getRedirectResult(auth).then((result) => {
      if (result?.user) {
        const email = (result.user.email ?? '').trim().toLowerCase();
        if (!isAdminEmail(email)) {
          void firebaseSignOut(auth);
          return;
        }
        const phone = getAdminPhone(result.user.uid);
        const u = firebaseUserToAdminUser(result.user, phone);
        setUser(u);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(u));
      }
    }).catch(() => { /* ignora erros de redirect (ex: usuário cancelou) */ });

    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        const email = (fbUser.email ?? '').trim().toLowerCase();
        if (!isAdminEmail(email)) {
          void firebaseSignOut(auth);
          setUser(null);
          localStorage.removeItem(AUTH_STORAGE_KEY);
          return;
        }
        const phone = getAdminPhone(fbUser.uid);
        const u = firebaseUserToAdminUser(fbUser, phone);
        setUser(u);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(u));
      } else {
        setUser(null);
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    });
    return () => unsub();
  }, []);

  const login = async (email: string, password: string): Promise<{ ok: boolean; error?: string }> => {
    const normalized = email.trim().toLowerCase();
    if (!isAdminEmail(normalized)) {
      return { ok: false, error: 'Acesso restrito. Apenas administradores autorizados.' };
    }

    const auth = getFirebaseAuth();
    if (!auth) {
      return { ok: false, error: 'Firebase não configurado. Configure as variáveis VITE_FIREBASE_* ou use Continuar com Google.' };
    }

    try {
      const result = await signInWithEmailAndPassword(auth, normalized, password);
      const phone = getAdminPhone(result.user.uid);
      const u = firebaseUserToAdminUser(result.user, phone);
      setUser(u);
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(u));
      return { ok: true };
    } catch (err: unknown) {
      const code = getFirebaseAuthErrorCode(err);
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
        return { ok: false, error: 'E-mail ou senha incorretos.' };
      }
      return { ok: false, error: getFirebaseAuthErrorMessage(err, 'login') };
    }
  };

  const loginWithGmail = async (): Promise<{ ok: boolean; error?: string }> => {
    const auth = getFirebaseAuth();
    if (!auth) return { ok: false, error: 'Firebase não configurado. Configure as variáveis de ambiente.' };

    try {
      // Popup funciona em localhost; redirect tem bug HTTP/HTTPS no Firebase
      const result = await signInWithPopup(auth, createGoogleAuthProvider());
      const email = (result.user.email ?? '').trim().toLowerCase();
      if (!isAdminEmail(email)) {
        await firebaseSignOut(auth);
        return { ok: false, error: 'Acesso restrito. Apenas administradores autorizados.' };
      }
      const phone = getAdminPhone(result.user.uid);
      const u = firebaseUserToAdminUser(result.user, phone);
      setUser(u);
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(u));
      return { ok: true };
    } catch (err: unknown) {
      const code = getFirebaseAuthErrorCode(err);
      if (code === 'auth/popup-blocked') {
        return {
          ok: false,
          error: getFirebaseAuthErrorMessage(err, 'google'),
        };
      }
      if (isFirebasePopupClosedByUser(err)) {
        return { ok: false };
      }
      return { ok: false, error: getFirebaseAuthErrorMessage(err, 'google') };
    }
  };

  const logout = async () => {
    const auth = getFirebaseAuth();
    if (auth) {
      try {
        await firebaseSignOut(auth);
      } catch {
        /* ignore */
      }
    }
    setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  const refreshUser = () => {
    const auth = getFirebaseAuth();
    if (!auth?.currentUser) return;
    const fb = auth.currentUser;
    const phone = getAdminPhone(fb.uid);
    const u = firebaseUserToAdminUser(fb, phone);
    setUser(u);
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(u));
  };

  const value = useMemo(
    () => ({
      user,
      login,
      loginWithGmail,
      logout,
      refreshUser,
      isFirebaseReady,
    }),
    [user, isFirebaseReady]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
